import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';
import { calculatePayout } from '../utils/roulette';

const INITIAL_BALANCE = 1000;

export const useRoulette = () => {
  const [balance, setBalance] = useState(() => getStorageItem('roulette_balance', INITIAL_BALANCE));
  const [currentBets, setCurrentBets] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [recentSpins, setRecentSpins] = useState(() => getStorageItem('roulette_recent_spins', []));
  const [lastWinningNumber, setLastWinningNumber] = useState(null);
  const [selectedChip, setSelectedChip] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [winningBets, setWinningBets] = useState([]);
  const [totalWon, setTotalWon] = useState(0);

  // Sync balance and spins to storage
  useEffect(() => { setStorageItem('roulette_balance', balance); }, [balance]);
  useEffect(() => { setStorageItem('roulette_recent_spins', recentSpins); }, [recentSpins]);

  const totalBetAmount = currentBets.reduce((sum, bet) => sum + (bet.amount || 0), 0);

  const placeBet = useCallback((betType, customChip) => {
    if (isSpinning) return;
    const chipValue = customChip || selectedChip;
    if (balance < chipValue) return;

    let parsedBet = null;
    const typeStr = String(betType).trim();

    if (typeStr === 'red' || typeStr === 'black') {
      parsedBet = { type: 'color', value: typeStr, betKey: typeStr };
    } else if (typeStr === 'even' || typeStr === 'odd') {
      parsedBet = { type: 'parity', value: typeStr, betKey: typeStr };
    } else if (typeStr === '1-18' || typeStr === '19-36') {
      parsedBet = { type: 'half', value: typeStr, betKey: typeStr };
    } else if (typeStr === '1st12' || typeStr === '1st 12') {
      parsedBet = { type: 'dozen', value: '1st 12', betKey: '1st12' };
    } else if (typeStr === '2nd12' || typeStr === '2nd 12') {
      parsedBet = { type: 'dozen', value: '2nd 12', betKey: '2nd12' };
    } else if (typeStr === '3rd12' || typeStr === '3rd 12') {
      parsedBet = { type: 'dozen', value: '3rd 12', betKey: '3rd12' };
    } else {
      const numVal = Number(typeStr);
      if (!isNaN(numVal) && numVal >= 0 && numVal <= 36) {
        parsedBet = { type: 'number', value: numVal, betKey: String(numVal) };
      }
    }

    if (!parsedBet) return;

    setBalance(prev => prev - chipValue);
    setCurrentBets(prev => {
      const existingIndex = prev.findIndex(b => b.betKey === parsedBet.betKey);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          amount: updated[existingIndex].amount + chipValue
        };
        return updated;
      }
      return [...prev, { ...parsedBet, amount: chipValue }];
    });
  }, [balance, selectedChip, isSpinning]);

  const clearBets = useCallback(() => {
    if (isSpinning) return;
    setBalance(prev => prev + totalBetAmount);
    setCurrentBets([]);
  }, [totalBetAmount, isSpinning]);

  const resetBalance = useCallback(() => {
    if (isSpinning) return;
    setBalance(INITIAL_BALANCE);
    setCurrentBets([]);
    setRecentSpins([]);
    setLastWinningNumber(null);
    setShowModal(false);
  }, [isSpinning]);

  const spinWheel = useCallback(() => {
    if (isSpinning || currentBets.length === 0) return;

    setIsSpinning(true);
    setLastWinningNumber(null);
    setShowModal(false);

    const winningNumber = Math.floor(Math.random() * 37);

    setTimeout(() => {
      let payoutTotal = 0;
      const winners = [];

      currentBets.forEach(bet => {
        const payout = calculatePayout(bet, winningNumber);
        if (payout > 0) {
          payoutTotal += payout;
          winners.push(bet);
        }
      });

      setBalance(prev => prev + payoutTotal);
      setLastWinningNumber(winningNumber);
      setRecentSpins(prev => [winningNumber, ...prev].slice(0, 10));
      setWinningBets(winners);
      setTotalWon(payoutTotal);
      setCurrentBets([]);
      setIsSpinning(false);
      setShowModal(true);
    }, 4000);
  }, [currentBets, isSpinning]);

  return {
    balance,
    currentBets,
    isSpinning,
    recentSpins,
    history: recentSpins,
    lastWinningNumber,
    selectedChip,
    setSelectedChip,
    placeBet,
    clearBets,
    resetBalance,
    spin: spinWheel,
    spinWheel,
    totalBetAmount,
    showModal,
    setShowModal,
    winningBets,
    totalWon
  };
};
